import type { DesignerPageOrientation, DesignerPageSettings, DesignerPageSize } from "./reportDesignerModel";

export type Point = { x: number; y: number };
export type Size = { width: number; height: number };
export type Box = Point & Size;

const PAGE_SIZES_PT: Record<DesignerPageSize, Size> = {
  letter: { width: 612, height: 792 },
  legal: { width: 612, height: 1008 },
};

export function pageSizePoints(size: DesignerPageSize, orientation: DesignerPageOrientation): Size {
  const base = PAGE_SIZES_PT[size];
  return orientation === "landscape"
    ? { width: base.height, height: base.width }
    : { ...base };
}

export function printableArea(page: DesignerPageSettings): Box {
  const size = pageSizePoints(page.size, page.orientation);
  const margins = page.margins || {};
  const left = margins.left ?? 24;
  const top = margins.top ?? 24;
  const right = margins.right ?? 24;
  const bottom = margins.bottom ?? 24;
  return {
    x: left,
    y: top,
    width: Math.max(0, size.width - left - right),
    height: Math.max(0, size.height - top - bottom),
  };
}

export function pointToScreen(point: Point, zoomPercent: number): Point {
  const scale = zoomPercent / 100;
  return { x: point.x * scale, y: point.y * scale };
}

export function screenToPoint(point: Point, zoomPercent: number): Point {
  const scale = zoomPercent / 100 || 1;
  return { x: point.x / scale, y: point.y / scale };
}

export function normalizeZoom(value: number): number {
  if (!Number.isFinite(value)) return 100;
  return Math.min(200, Math.max(25, value));
}

export function containsPoint(box: Box, point: Point): boolean {
  return point.x >= box.x && point.y >= box.y && point.x <= box.x + box.width && point.y <= box.y + box.height;
}

export function boxesIntersect(a: Box, b: Box): boolean {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}

export function columnWidthToCss(width: number | undefined): string {
  const safeWidth = Number.isFinite(Number(width)) ? Math.max(4, Number(width)) : 12;
  return `${safeWidth * 8}px`;
}
